import { useState } from 'react';
import { useField } from 'formik';

import {
  Credential,
  CredentialType,
} from '@/react/portainer/settings/sharedCredentials/types';
import { CredentialsForm } from '@/react/portainer/settings/sharedCredentials/CreateCredentialsView/CredentialsForm';

import { FormControl } from '@@/form-components/FormControl';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { Sheet, SheetContent, SheetDescription, SheetHeader } from '@@/Sheet';

import { FormValues } from './types';

interface Props {
  credentials: Array<Credential>;
}

export function SSHCredentialField({ credentials }: Props) {
  const [isAdding, setIsAdding] = useState(false);
  const [field, meta, helpers] =
    useField<FormValues['credentialId']>('credentialId');
  const options = credentials.map((c) => ({ value: c.id, label: c.name }));

  return (
    <>
      <FormControl
        label="SSH credential"
        inputId="kubesolo-ssh-credential"
        required
        errors={meta.touched && meta.error}
      >
        <PortainerSelect<FormValues['credentialId']>
          inputId="kubesolo-ssh-credential"
          name={field.name}
          options={options}
          value={field.value}
          onChange={handleChange}
          placeholder="Select a credential"
          isCreatable
          // always offer it, not only once something is typed
          isValidNewOption={() => true}
          formatCreateLabel={() => 'Add credential'}
          onCreateOption={() => setIsAdding(true)}
          data-cy="kubesolo-ssh-credential-select"
        />
      </FormControl>

      <Sheet open={isAdding} onOpenChange={setIsAdding}>
        <SheetContent className="flex flex-col">
          {/* cancel the header's column gutter, so the title lines up with the form */}
          <div className="-mx-[15px]">
            <SheetHeader title="Add SSH credential" />
          </div>
          <SheetDescription className="sr-only">
            Add an SSH credential to install KubeSolo with.
          </SheetDescription>
          <CredentialsForm
            credentialType={CredentialType.SSH}
            onCreate={handleCreate}
            inDrawer
          />
        </SheetContent>
      </Sheet>
    </>
  );

  function handleChange(value: FormValues['credentialId'] | null) {
    if (value) {
      helpers.setValue(value);
    }
  }

  function handleCreate(credential: Credential) {
    helpers.setValue(credential.id);
    setIsAdding(false);
  }
}
